/**
 * Pure playback decisions of the web player: what to do with an audio error,
 * whether a scheduled retry is still wanted, whether a missing track is
 * skipped. Kept free of `/@/` imports and DOM globals so
 * `tests/playback-guards.test.mts` can drive them.
 */

/** `MediaError` codes (the DOM constants, so this module does not need a browser). */
export const MEDIA_ERROR = {
    ABORTED: 1,
    DECODE: 3,
    NETWORK: 2,
    SRC_NOT_SUPPORTED: 4,
} as const;

export const MAX_MEDIA_RETRIES = 5;

export type MediaErrorAction = 'ignore' | 'pause' | 'retry' | 'skip';

/**
 * An audio element failed. Only a queue the user is playing right now is
 * retried, skipped or paused: an element that failed while paused (a restored
 * queue preloading its tracks) is left alone, so it neither keeps hitting the
 * server nor starts playing by itself. It is loaded again once the user asks
 * for playback.
 */
export const mediaErrorAction = ({
    code,
    isPlaying,
    retryCount,
}: {
    code: number | undefined;
    isPlaying: boolean;
    retryCount: number;
}): MediaErrorAction => {
    if (!isPlaying) return 'ignore';
    if (code === MEDIA_ERROR.DECODE) return 'skip';
    if (code === MEDIA_ERROR.NETWORK || code === MEDIA_ERROR.SRC_NOT_SUPPORTED) {
        return retryCount < MAX_MEDIA_RETRIES ? 'retry' : 'pause';
    }
    return 'ignore';
};

/**
 * A retry scheduled after an error runs only if, when its timer fires, the
 * user still wants playback and the element still holds the source that
 * failed (not the next track, not a source of another sign-in).
 */
export const isRetryStillWanted = ({
    currentSrc,
    failedSrc,
    isMounted,
    isPlaying,
}: {
    currentSrc: null | string | undefined;
    failedSrc: null | string | undefined;
    isMounted: boolean;
    isPlaying: boolean;
}): boolean => isMounted && isPlaying && Boolean(failedSrc) && currentSrc === failedSrc;
